const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]", "::1", "0.0.0.0"])

export interface AppBaseUrlInput {
  /** `window.location.origin`, or "" when there is no window (SSR / prerender). */
  origin: string
  /** `window.location.hostname`; derived from `origin` when missing. */
  hostname?: string
  /** Build-time `VITE_PUBLIC_APP_URL`, e.g. https://www.marginwiz.com. */
  public_app_url?: string
  /** True when the app runs inside another frame/webview whose origin is not the app's. */
  embedded: boolean
}

/**
 * Base URL for links that leave the browser (CSV exports, copied links).
 *
 * `window.location.origin` is right for a normal browser tab, but a local dev server or an
 * embedded webview reports `localhost` (or an opaque origin), and exported links then point at the
 * reader's own machine. In those cases prefer the configured public URL when one is set.
 */
export function resolve_app_base_url({ origin, hostname, public_app_url, embedded }: AppBaseUrlInput): string {
  const configured = public_app_url?.trim().replace(/\/+$/, "")
  const host = (hostname || origin.replace(/^https?:\/\//, "").split(":")[0]).toLowerCase()
  const is_local = LOCAL_HOSTNAMES.has(host)
  const is_opaque = origin === "" || origin === "null" || !/^https?:\/\//.test(origin)
  if (configured && (is_local || embedded || is_opaque)) {
    return configured
  }
  return origin === "null" ? "" : origin
}

function is_embedded(): boolean {
  try {
    return window.self !== window.top
  } catch {
    // Cross-origin parent: accessing window.top throws.
    return true
  }
}

/** Production app URL, matching the `VITE_SITE_ORIGIN` default in `blog/prerender_head.ts`. */
const DEFAULT_PUBLIC_APP_URL = "https://www.marginwiz.com"

/** Avoid reading `window` at module load — SSR / prerender has no `window`. */
export function app_base_url(): string {
  const public_app_url = (import.meta.env.VITE_PUBLIC_APP_URL as string | undefined) || DEFAULT_PUBLIC_APP_URL
  if (typeof window === "undefined") {
    return public_app_url.trim().replace(/\/+$/, "")
  }
  return resolve_app_base_url({
    origin: window.location.origin,
    hostname: window.location.hostname,
    public_app_url,
    embedded: is_embedded(),
  })
}
